import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import api from '@/utils/api';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { Calendar, Clock, Users, Video } from 'lucide-react';

export default function Events() {
  const { t, localizedField, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');

  useEffect(() => {
    api.get('/events').then(r => { setEvents(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const upcoming = events
    .filter(e => e.status === 'upcoming' || e.status === 'live')
    .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
  const past = events
    .filter(e => e.status === 'ended')
    .sort((a, b) => new Date(b.event_date) - new Date(a.event_date));

  const currentEvent = upcoming[0] || null;
  const otherUpcoming = upcoming.slice(1);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(lang === 'ar' ? 'ar' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString(lang === 'ar' ? 'ar' : 'en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    if (status === 'live') return <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 text-xs font-body animate-pulse">{lang === 'ar' ? 'مباشر' : 'LIVE'}</span>;
    if (status === 'ended') return <span className="px-2 py-0.5 rounded-full bg-dust-gray/10 text-dust-gray text-xs font-body">{lang === 'ar' ? 'انتهى' : 'Ended'}</span>;
    return <span className="px-2 py-0.5 rounded-full bg-sacred-gold/10 text-sacred-gold text-xs font-body">{lang === 'ar' ? 'قادم' : 'Upcoming'}</span>;
  };

  const renderEventCard = (event, variant = 'normal') => {
    const isCurrent = variant === 'current';
    const isFaded = variant === 'faded';
    return (
      <Link
        to={`/events/${event.slug}`}
        className={`group block rounded-2xl transition-all duration-300 ${isCurrent ? 'glass-card p-8 md:p-10 ring-2 ring-sacred-gold/30 shadow-xl hover:shadow-2xl' : isFaded ? 'glass-card p-4 opacity-60 hover:opacity-100 hover:shadow-md' : 'glass-card p-6 hover:shadow-lg'}`}
        data-testid={`event-card-${event.slug}`}
      >
        <div className={`flex flex-col sm:flex-row gap-5 ${isCurrent ? 'items-center' : ''}`}>
          <div className={`${isCurrent ? 'sm:w-32' : isFaded ? 'sm:w-16' : 'sm:w-24'} shrink-0 text-center`}>
            <div className="inline-block sm:block">
              <div className={`text-sacred-gold font-body uppercase tracking-wider ${isCurrent ? 'text-sm' : 'text-xs'}`}>
                {event.event_date ? new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' }) : ''}
              </div>
              <div className={`font-display text-heading ${isCurrent ? 'text-5xl md:text-6xl' : isFaded ? 'text-2xl' : 'text-3xl'}`}>
                {event.event_date ? new Date(event.event_date).getDate() : ''}
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              {isCurrent && (
                <span className="px-2.5 py-0.5 rounded-full bg-sacred-gold text-white text-xs font-body font-semibold" data-testid="current-event-badge">
                  {lang === 'ar' ? 'الحدث الحالي' : 'Current Event'}
                </span>
              )}
              {getStatusBadge(event.status)}
              {event.is_free ? (
                <span className="px-2 py-0.5 rounded-full bg-accent-success/10 text-accent-success text-xs font-body">{t('events.free')}</span>
              ) : (
                <span className="px-2 py-0.5 rounded-full bg-heading/5 text-heading text-xs font-body">${event.price_usd}</span>
              )}
            </div>

            <h3 className={`font-display text-heading mb-2 group-hover:text-sacred-gold transition-colors ${isCurrent ? 'text-2xl md:text-3xl' : isFaded ? 'text-base' : 'text-xl'}`} data-testid={`event-title-${event.slug}`}>
              {localizedField(event.title)}
            </h3>
            <p className={`text-text-muted font-body mb-3 line-clamp-2 ${isCurrent ? 'text-base' : 'text-sm'}`}>
              {localizedField(event.description)}
            </p>

            <div className="flex flex-wrap items-center gap-4 text-xs text-dust-gray font-body">
              <span className="flex items-center gap-1"><Calendar size={13} /> {formatDate(event.event_date)}</span>
              <span className="flex items-center gap-1"><Clock size={13} /> {formatTime(event.event_date)}</span>
              {event.duration_minutes && <span className="flex items-center gap-1"><Clock size={13} /> {event.duration_minutes}min</span>}
              <span className="flex items-center gap-1"><Users size={13} /> {event.registered_count || 0}{event.max_attendees ? `/${event.max_attendees}` : ''}</span>
              <span className="flex items-center gap-1"><Video size={13} /> {lang === 'ar' ? 'جلسة مباشرة' : 'Live Session'}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div data-testid="events-page" className="min-h-screen">
      <section className="pt-12 pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <ScrollReveal>
            <h1 className="text-4xl sm:text-5xl font-display text-heading mb-4" data-testid="events-title">{t('events.title')}</h1>
            <p className="text-base text-text-muted font-body max-w-2xl mx-auto">
              {lang === 'ar'
                ? 'جلسات مباشرة وورش عمل ولقاءات مجتمعية. تواصل مع طارق والمجتمع في الوقت الفعلي.'
                : 'Live sessions, workshops, and community gatherings. Connect with Tariq and the community in real time.'}
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="pb-24 px-4">
        <div className="max-w-4xl mx-auto">
          <ScrollReveal>
            <div className="flex gap-3 mb-8 justify-center" data-testid="event-filters">
              {['upcoming', 'past'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-5 py-2 rounded-full font-body text-sm transition-all ${filter === f ? 'gold-button' : 'glass-button text-text-muted hover:text-heading'}`}
                  data-testid={`filter-${f}`}
                >
                  {f === 'upcoming' ? t('events.upcoming') : t('events.past')}
                </button>
              ))}
            </div>
          </ScrollReveal>

          {loading ? (
            <div className="text-center text-text-muted font-body py-20" data-testid="events-loading">{t('common.loading')}</div>
          ) : filter === 'upcoming' ? (
            upcoming.length === 0 ? (
              <ScrollReveal>
                <div className="text-center py-20" data-testid="events-empty">
                  <div className="w-20 h-20 rounded-full bg-sacred-gold/10 flex items-center justify-center mx-auto mb-6">
                    <Calendar size={32} className="text-sacred-gold" />
                  </div>
                  <h2 className="text-2xl font-display text-heading mb-3">{lang === 'ar' ? 'لا توجد فعاليات قادمة' : 'No upcoming events'}</h2>
                  <p className="text-text-muted font-body">{lang === 'ar' ? 'ترقّبوا اللقاءات القادمة.' : 'Stay tuned for upcoming gatherings.'}</p>
                </div>
              </ScrollReveal>
            ) : (
              <div className="space-y-8">
                {currentEvent && (
                  <ScrollReveal>
                    {renderEventCard(currentEvent, 'current')}
                  </ScrollReveal>
                )}
                {otherUpcoming.length > 0 && (
                  <div>
                    <h3 className="text-sm text-text-muted/70 font-body uppercase tracking-wider mb-4" data-testid="more-upcoming-heading">
                      {lang === 'ar' ? 'المزيد من الأحداث القادمة' : 'More Upcoming Events'}
                    </h3>
                    <div className="space-y-3">
                      {otherUpcoming.map((e, i) => (
                        <ScrollReveal key={e.id} delay={i * 0.05}>
                          {renderEventCard(e, 'faded')}
                        </ScrollReveal>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          ) : (
            // Past events
            past.length === 0 ? (
              <ScrollReveal>
                <div className="text-center py-20" data-testid="past-events-empty">
                  <div className="w-20 h-20 rounded-full bg-dust-gray/10 flex items-center justify-center mx-auto mb-6">
                    <Calendar size={32} className="text-dust-gray" />
                  </div>
                  <h2 className="text-2xl font-display text-heading mb-3">
                    {lang === 'ar' ? 'لا توجد فعاليات سابقة' : 'No past events yet'}
                  </h2>
                  <p className="text-text-muted font-body">
                    {lang === 'ar' ? 'ستظهر الفعاليات المنتهية هنا بعد انعقادها.' : 'Expired events will appear here once they\'ve taken place.'}
                  </p>
                </div>
              </ScrollReveal>
            ) : (
              <div className="space-y-3" data-testid="past-events-list">
                {past.map((e, i) => (
                  <ScrollReveal key={e.id} delay={i * 0.05}>
                    {renderEventCard(e, 'faded')}
                  </ScrollReveal>
                ))}
              </div>
            )
          )}
        </div>
      </section>
    </div>
  );
}
